import { io } from 'socket.io-client';
import { resolveServerUrl } from './utils/serverUrl';
import { debugLog, debugWarn } from './utils/debug';
import { auth } from './firebase';

const SERVER_URL = resolveServerUrl();
debugLog('Socket.IO server URL:', SERVER_URL);

let socket = null;
let lastAuthToken = null; // Persist last token for watchdog reconnects
const socketChangeCallbacks = new Set();

// Heartbeat/watchdog state
let heartbeatIntervalId = null;
let heartbeatWatchdogId = null;
let lastHeartbeatAckAt = 0;

// Track last successful data sync for staleness detection
let lastDataReceivedAt = Date.now();

// When a media upload is in progress we must NOT tear down and recreate the socket
// (forceNew), because the pending message:send emit would be dropped and the upload lost.
let uploadsInFlight = false;
export const setUploadsInFlight = (active) => { uploadsInFlight = !!active; };
export const hasUploadsInFlight = () => uploadsInFlight;

export const updateLastDataReceived = () => {
  lastDataReceivedAt = Date.now();
};

export const getLastDataReceivedAt = () => lastDataReceivedAt;

// Check if socket is truly healthy (connected AND receiving data)
export const isSocketHealthy = () => {
  if (!socket || !socket.connected) return false;
  // If we haven't received any data in 30s, consider unhealthy
  const staleness = Date.now() - lastDataReceivedAt;
  return staleness < 30000;
};

const clearHeartbeat = () => {
  if (heartbeatIntervalId) {
    clearInterval(heartbeatIntervalId);
    heartbeatIntervalId = null;
  }
  if (heartbeatWatchdogId) {
    clearInterval(heartbeatWatchdogId);
    heartbeatWatchdogId = null;
  }
};

const startHeartbeat = () => {
  if (!socket) return;

  // Reset any existing loops
  clearHeartbeat();

  lastHeartbeatAckAt = Date.now();

  // Listen for server ack
  socket.off('server:heartbeat');
  socket.on('server:heartbeat', () => {
    lastHeartbeatAckAt = Date.now();
    lastDataReceivedAt = Date.now(); // Also update data received timestamp
  });

  // Send client heartbeat every 20s
  heartbeatIntervalId = setInterval(() => {
    try {
      if (socket && socket.connected) {
        socket.emit('client:heartbeat', { t: Date.now() });
      }
    } catch (e) {
      // no-op
    }
  }, 20000);

  // Watchdog: if no ack for 45s, force a reconnect
  heartbeatWatchdogId = setInterval(async () => {
    const now = Date.now();
    const msSinceAck = now - lastHeartbeatAckAt;
    if (msSinceAck > 45000) {
      // Never recreate the socket while an upload is in flight — a forceNew reconnect would
      // drop the pending message:send and the media would be lost (file uploaded, no message).
      if (uploadsInFlight) {
        console.warn('🔌 Heartbeat stalled but an upload is in flight — deferring reconnect');
        return;
      }
      console.warn('🔌 Heartbeat stalled (', msSinceAck, 'ms). Forcing socket reconnect...');
      try {
        if (socket) {
          socket.disconnect();
        }
      } catch (_) {}
      socket = null;
      // Recreate connection using last known token
      if (lastAuthToken) {
        connectSocket(lastAuthToken);
      }
    }
  }, 10000);
};

export const connectSocket = (token) => {
  // If a socket already exists and is either connected or mid-connection,
  // reuse it. Disconnecting a socket during its initial handshake can cause
  // "WebSocket is closed before the connection is established" on first load.
  const isMidConnect =
    !!socket &&
    !socket.connected &&
    socket.io &&
    socket.io.engine &&
    socket.io.engine.readyState === 'opening';
  if (socket && (socket.connected || isMidConnect)) {
    lastAuthToken = token || lastAuthToken;
    debugLog('Socket already active (connected or connecting), reusing');
    return socket;
  }

  // Disconnect existing socket if any
  if (socket) {
    debugLog('Disconnecting existing socket before reconnecting');
    socket.disconnect();
    socket = null;
  }

  debugLog('Creating new socket connection...');
  lastAuthToken = token || lastAuthToken;
  
  // On native (Capacitor) prefer websocket-only to avoid long HTTP fallback/upgrade
  const isNative = typeof window !== 'undefined' && window.Capacitor && typeof window.Capacitor.getPlatform === 'function' && window.Capacitor.getPlatform() !== 'web';
  const transports = isNative ? ['websocket'] : ['websocket', 'polling'];
  
  // Detect platform: mobile app vs mobile browser vs desktop web
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
  const isMobileBrowser = /Android|iPhone|iPad|iPod|Mobi/i.test(ua) || (window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
  const platform = isNative ? 'mobile' : (isMobileBrowser ? 'mobile' : 'web');
  
  socket = io(SERVER_URL, {
    // A callback, not a fixed object: Socket.IO calls it on every (re)connect. Cloud Run cuts
    // each WebSocket at 300s, and a fixed object kept resending the original ID token, which
    // expires after an hour, so the server rejected the reconnect and the app sat offline.
    // getIdToken() returns the cached token, refreshing it only when it's near expiry.
    auth: (cb) => {
      const currentUser = auth.currentUser;
      const tokenPromise = currentUser ? currentUser.getIdToken() : Promise.resolve(lastAuthToken);
      tokenPromise
        .then((freshToken) => {
          if (freshToken) lastAuthToken = freshToken;
          cb({ token: lastAuthToken, platform });
        })
        .catch(() => cb({ token: lastAuthToken, platform }));
    },
    autoConnect: true,
    transports,
    upgrade: !isNative,
    rememberUpgrade: false,
    timeout: 8000,
    forceNew: true,
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 6000,
    reconnectionAttempts: Infinity,
    closeOnBeforeunload: true
  });

  socket.on('connect', () => {
    debugLog('Socket connected successfully');
    // Notify server that user is back online
    try {
      socket.emit('user:online');
    } catch (error) {
      console.warn('⚠️ Could not emit online status:', error);
    }

    // Start heartbeat/watchdog
    startHeartbeat();
  });

  socket.on('user:offline:ack', () => {
    debugLog('Received offline acknowledgment from server');
  });

  socket.on('disconnect', (reason) => {
    debugLog('Socket disconnected:', reason);
    // Stop heartbeat timers to avoid leaks
    clearHeartbeat();
  });

  const thisSocket = socket;
  socket.on('connect_error', (error) => {
    console.error('🔌 Socket connection error:', error);
    // When the server's auth middleware rejects us, socket.io-client gives up for good
    // (socket.active === false) instead of retrying. Retry ourselves; the auth callback
    // above fetches a fresh token for the attempt.
    if (!thisSocket.active) {
      setTimeout(() => {
        if (socket === thisSocket && !thisSocket.connected && !thisSocket.active) {
          debugLog('Retrying socket connection after rejected handshake');
          thisSocket.connect();
        }
      }, 1000 + Math.floor(Math.random() * 2000));
    }
  });

  socket.on('reconnect', (attemptNumber) => {
    debugLog('Socket reconnected after', attemptNumber, 'attempts');
    // Restart heartbeat on successful reconnect
    startHeartbeat();
  });

  socket.on('reconnect_error', (error) => {
    console.error('🔌 Socket reconnection error:', error);
  });

  socket.on('reconnect_failed', () => {
    console.error('🔌 Socket reconnection failed');
  });

  // Notify all listeners that socket changed
  debugLog('Notifying', socketChangeCallbacks.size, 'listeners of socket change');
  socketChangeCallbacks.forEach(callback => {
    try {
      callback(socket);
    } catch (error) {
      debugWarn('Error in socket change callback:', error);
    }
  });

  return socket;
};

export const disconnectSocket = () => {
  if (socket) {
    debugLog('Disconnecting socket...', { connected: socket.connected, id: socket.id });
    
    // For mobile apps, we need to disconnect IMMEDIATELY without waiting
    // because Android/iOS will suspend JavaScript execution when backgrounded
    // The server's 'disconnect' handler will mark the user as offline
    if (socket.connected) {
      try {
        // Use volatile emit - it won't block or retry if it fails
        socket.volatile.emit('user:offline');
        socket.disconnect();
        socket = null;
        debugLog('Socket disconnected');
      } catch (error) {
        debugWarn('Error during disconnect:', error);
        try {
          socket.disconnect();
        } catch (e) {
          debugWarn('Error calling disconnect:', e);
        }
        socket = null;
      }
    } else {
      debugLog('Socket already disconnected');
      socket = null;
    }

    // Always clear heartbeat timers on explicit disconnect
    clearHeartbeat();
  } else {
    debugLog('No socket to disconnect');
  }
};

export const getSocket = () => socket;

export const isSocketConnected = () => {
  return socket && socket.connected;
};

export const reconnectSocket = async (token) => {
  console.log('🔌 Attempting to reconnect socket...');
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  return connectSocket(token);
};

// Force reconnect - more aggressive, clears all state and timers
export const forceReconnect = async (token) => {
  console.log('🔌 Force reconnecting socket...');
  clearHeartbeat();
  if (socket) {
    try {
      socket.removeAllListeners();
      socket.disconnect();
    } catch (_) {}
    socket = null;
  }
  lastDataReceivedAt = Date.now(); // Reset staleness
  return connectSocket(token || lastAuthToken);
};

// Check connection and reconnect if unhealthy - returns promise that resolves when connected
export const ensureConnection = async (token) => {
  const tokenToUse = token || lastAuthToken;
  
  // Already connected and healthy
  if (socket && socket.connected) {
    lastAuthToken = tokenToUse || lastAuthToken;
    return socket;
  }

  // Mid-handshake: let it finish. Several paths (room change, resume, health check) call
  // this within ~100ms of each other, and rebuilding here kept killing the attempt the
  // previous caller had just started, which is slowest exactly on a cold start.
  if (socket && socket.io && socket.io.engine && socket.io.engine.readyState === 'opening') {
    lastAuthToken = tokenToUse || lastAuthToken;
    return socket;
  }

  // Not connected - force reconnect
  console.log('🔌 ensureConnection: socket not connected, reconnecting...');
  return forceReconnect(tokenToUse);
};

// Subscribe to socket changes (when socket is recreated after disconnect/reconnect)
export const onSocketChange = (callback) => {
  socketChangeCallbacks.add(callback);
  // Immediately call with current socket if it exists
  if (socket) {
    callback(socket);
  }
  // Return unsubscribe function
  return () => socketChangeCallbacks.delete(callback);
};

export default { 
  connectSocket, 
  disconnectSocket, 
  getSocket, 
  isSocketConnected, 
  isSocketHealthy,
  reconnectSocket, 
  forceReconnect,
  ensureConnection,
  onSocketChange,
  updateLastDataReceived,
  getLastDataReceivedAt
};

